// ArtworkFilePicker — shared artwork-attach surface (SO→PO rebuild Round 2,
// 2026-07-27). Three tabs, matching commonsku's per-location artwork image
// picker (map §Stage 4: "picker over Project Files / Client Files + Upload"):
//
//   • Upload New    — uploads THROUGH the project (POST /projects/:id/files,
//                     tag "artwork", file_purpose "logo") so every logo both
//                     attaches to the decoration AND lands in the project's
//                     Files gallery (review A5). Returns the same files.id used
//                     as logo_file_id.
//   • Project Files — the project's existing files (GET /projects/:id/files);
//                     pick one to attach.
//   • Client Library — client-wide art reuse. The cross-project aggregator
//                     (GET /clients/:id/files) isn't built yet, so this tab
//                     points at the two working tabs (review A10).
//   • From commonsku — (large picker only, 2026-11-02) the client's old
//                     commonsku art with "Add & use" (CommonskuArt.tsx).
//
// It never writes logo_file_id itself: selecting a file (upload OR library)
// calls `onSelect(fileId)`; the caller (OrderDecorationRow) PATCHes the
// decoration. So the single write path stays the existing decoration PATCH.
// The attached-logo strip resolves a real filename + image thumbnail via the
// staff GET /files/:id metadata route (review A6) — no more raw UUID.
//
// Wired into OrderLineEditor only for now (the presentation call-site is a
// documented follow-up).
import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ImagePlus, Library } from "lucide-react";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import { CommonskuArtList } from "@/components/artwork/CommonskuArt";

type PickerTab = "upload" | "project" | "library";

interface ProjectFileRow {
  id: string;
  tag: string;
  fileId: string;
  originalFilename: string;
  mimeType: string;
  sizeBytes: number;
}
interface ProjectFilesListResponse {
  rows: ProjectFileRow[];
  nextCursor: unknown;
}
interface DownloadResponse {
  url: string;
  expires_at: string;
  mime_type: string;
  original_filename: string;
}
interface FileMeta {
  id: string;
  original_filename: string;
  mime_type: string;
  size_bytes: number;
  is_image: boolean;
  view_url: string | null;
  /** A picture of PDF / AI / EPS art (Stephanie 2026-10-26). */
  preview_url?: string | null;
}

function isImageMime(mime: string): boolean {
  return mime.startsWith("image/");
}

export function ArtworkFilePicker(props: {
  projectId: string;
  logoFileId: string | null;
  onSelect: (fileId: string) => void;
  onClear: () => void;
  /** The quote item's Artwork window (Stephanie 2026-10-14, idea D): one big
   *  picture grid — an upload tile, then every project file as a large tile —
   *  instead of the three small tabs. */
  large?: boolean;
}) {
  const { projectId, logoFileId, onSelect, onClear, large } = props;
  const [tab, setTab] = useState<PickerTab>("upload");
  // The filename of a just-picked file, so the attached strip can show a name
  // instantly (review A6, ux SF2) before GET /files/:id resolves.
  const [justPicked, setJustPicked] = useState<{ id: string; name: string } | null>(
    null,
  );

  const handleSelect = (fileId: string, filename?: string): void => {
    if (filename) setJustPicked({ id: fileId, name: filename });
    onSelect(fileId);
  };

  const TABS: { id: PickerTab; label: string }[] = [
    { id: "upload", label: "Upload New" },
    { id: "project", label: "Project Files" },
    { id: "library", label: "Client Library" },
  ];

  if (large) {
    return (
      <div className="mt-4">
        <div className="mb-2 flex items-baseline justify-between gap-3">
          <span className="text-sm font-semibold text-brown-500">Pick the artwork</span>
          {logoFileId && (
            <button
              type="button"
              onClick={onClear}
              className="text-sm font-semibold text-brown-300 underline hover:text-rust-500"
            >
              Remove artwork
            </button>
          )}
        </div>
        <ProjectFilesPickTab
          projectId={projectId}
          selectedFileId={logoFileId}
          onSelect={handleSelect}
          large
          uploadTile={<UploadTab projectId={projectId} onSelect={handleSelect} tile />}
        />
        {/* The client's old commonsku art (Jake go 2026-11-02): "Add & use"
            adds it to the client's art + this project's Files and picks it.
            Hidden when the account has none. */}
        <CommonskuArtList
          projectId={projectId}
          selectedFileId={logoFileId}
          onUse={handleSelect}
        />
      </div>
    );
  }

  return (
    <div className="mt-3">
      <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-brown-300">
        Logo artwork
      </span>

      {logoFileId && (
        <AttachedLogoStrip
          fileId={logoFileId}
          fallbackName={justPicked?.id === logoFileId ? justPicked.name : null}
          onClear={onClear}
        />
      )}

      <div className="flex items-center gap-1 border-b border-peach-200">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={cn(
              "-mb-px border-b-2 px-2.5 py-1.5 text-[11px] font-semibold uppercase tracking-wide transition-colors",
              tab === t.id
                ? "border-ink text-ink"
                : "border-transparent text-brown-300 hover:text-brown-400",
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="pt-2">
        {tab === "upload" && (
          <UploadTab projectId={projectId} onSelect={handleSelect} />
        )}
        {tab === "project" && (
          <ProjectFilesPickTab
            projectId={projectId}
            selectedFileId={logoFileId}
            onSelect={handleSelect}
          />
        )}
        {tab === "library" && (
          <ClientLibraryComingSoon onGoToUpload={() => setTab("upload")} />
        )}
      </div>
    </div>
  );
}

// ─── Attached logo — real filename + image thumbnail (review A6) ──────────

function AttachedLogoStrip(props: {
  fileId: string;
  fallbackName: string | null;
  onClear: () => void;
}) {
  const { fileId, fallbackName, onClear } = props;
  const { data, isLoading } = useQuery({
    queryKey: ["file-meta", fileId],
    queryFn: () => api<FileMeta>(`/files/${fileId}`),
    staleTime: 5 * 60 * 1000,
  });

  const name = data?.original_filename ?? fallbackName ?? "Logo on file";

  return (
    <div className="mb-2 flex items-center gap-3 rounded-md border border-brown-50 bg-cream-50/40 p-1.5">
      <div className="flex h-12 w-12 flex-none items-center justify-center overflow-hidden rounded bg-cream-100">
        {(data?.is_image && data.view_url) || data?.preview_url ? (
          <img
            src={(data.is_image && data.view_url) || data.preview_url!}
            alt={name}
            className="h-full w-full object-contain"
          />
        ) : (
          <span className="font-display text-[9px] font-bold uppercase tracking-wide text-brown-300">
            {data ? data.mime_type.split("/")[1] ?? "File" : "…"}
          </span>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate text-xs font-semibold text-brown-500" title={name}>
          {name}
        </div>
        <div className="text-[10px] text-brown-300">
          {isLoading ? "Loading…" : "Attached to this location"}
        </div>
      </div>
      <button
        type="button"
        onClick={onClear}
        className="flex-none text-[10px] font-semibold text-brown-300 underline hover:text-rust-500"
      >
        Remove
      </button>
    </div>
  );
}

// ─── Upload New — upload through the project so it's discoverable (A5) ─────

function UploadTab(props: {
  projectId: string;
  onSelect: (fileId: string, filename: string) => void;
  /** Render as one big grid tile (the large picker). */
  tile?: boolean;
}) {
  const { projectId, onSelect, tile } = props;
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const upload = useMutation({
    mutationFn: async (file: File) => {
      // POST /projects/:id/files registers the files row AND a project_files
      // join row in one call (createProjectFile). file_purpose "logo" keeps the
      // file servable by the logo-scoped /files/:id metadata route.
      const reg = await api<{
        project_file_id: string;
        file_id: string;
        upload_url: string;
        live: boolean;
      }>(`/projects/${projectId}/files`, {
        method: "POST",
        body: JSON.stringify({
          filename: file.name,
          mime_type: file.type || "application/octet-stream",
          size_bytes: file.size,
          tag: "artwork",
          file_purpose: "logo",
        }),
      });
      if (reg.live) {
        const put = await fetch(reg.upload_url, {
          method: "PUT",
          headers: { "Content-Type": file.type || "application/octet-stream" },
          body: file,
        });
        if (!put.ok) {
          throw new Error(`Logo upload failed: ${put.status} ${put.statusText}`);
        }
      }
      return { fileId: reg.file_id, name: file.name };
    },
    onError: (err) =>
      setUploadError(err instanceof Error ? err.message : "Upload failed."),
    onSuccess: ({ fileId, name }) => {
      setUploadError(null);
      // The new logo is now a project file — refresh the Project Files tab.
      void queryClient.invalidateQueries({
        queryKey: ["project", projectId, "files"],
      });
      onSelect(fileId, name);
      if (inputRef.current) inputRef.current.value = "";
    },
  });

  if (tile) {
    return (
      <div>
        <input
          ref={inputRef}
          type="file"
          accept="image/png,image/jpeg,image/svg+xml,application/pdf,.ai,.eps"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) upload.mutate(f);
          }}
        />
        <button
          type="button"
          onClick={() => {
            setUploadError(null);
            inputRef.current?.click();
          }}
          disabled={upload.isPending}
          className="flex h-40 w-full flex-col items-center justify-center gap-2 rounded-md border-2 border-dashed border-peach-300 bg-cream-50/40 text-[15px] font-semibold text-brown-400 hover:border-peach-400 hover:bg-cream-50 disabled:opacity-50"
        >
          <ImagePlus className="h-6 w-6" />
          {upload.isPending ? "Uploading…" : "Upload new"}
          <span className="text-xs font-normal text-brown-300">PNG, JPG, SVG, PDF, AI, EPS</span>
        </button>
        {uploadError && <p className="mt-1 text-sm text-rust-600">{uploadError}</p>}
      </div>
    );
  }

  return (
    <div>
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/svg+xml,application/pdf,.ai,.eps"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) upload.mutate(f);
        }}
      />
      <button
        type="button"
        onClick={() => {
          setUploadError(null);
          inputRef.current?.click();
        }}
        disabled={upload.isPending}
        className="flex w-full items-center justify-center gap-2 rounded-md border border-dashed border-peach-300 bg-cream-50/40 px-3 py-3 text-xs font-semibold text-brown-400 hover:border-peach-400 hover:bg-cream-50 disabled:opacity-50"
      >
        <ImagePlus className="h-4 w-4" />
        {upload.isPending
          ? "Uploading…"
          : "Upload logo (PNG, JPG, SVG, PDF, AI, EPS)"}
      </button>
      <p className="mt-1 text-[10px] text-brown-300">
        Also saved to this project's Files, so it's reusable across locations.
      </p>
      {uploadError && (
        <p className="mt-1 text-[10px] text-rust-600">{uploadError}</p>
      )}
    </div>
  );
}

// ─── Project Files — reuse the project files list, pick one to attach ─────

function ProjectFilesPickTab(props: {
  projectId: string;
  selectedFileId: string | null;
  onSelect: (fileId: string, filename: string) => void;
  /** Big tiles, with the upload tile first (the large picker). */
  large?: boolean;
  uploadTile?: React.ReactNode;
}) {
  const { projectId, selectedFileId, onSelect, large, uploadTile } = props;
  const { data, isLoading, isError } = useQuery({
    queryKey: ["project", projectId, "files", "artwork-picker"],
    queryFn: () =>
      api<ProjectFilesListResponse>(`/projects/${projectId}/files?limit=100`),
  });

  const rows = data?.rows ?? [];

  if (large) {
    return (
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-4">
        {uploadTile}
        {isLoading && <p className="self-center text-sm text-brown-300">Loading files…</p>}
        {isError && <p className="self-center text-sm text-rust-500">Couldn't load project files.</p>}
        {rows.map((f) => (
          <ProjectFileTile
            key={f.id}
            projectId={projectId}
            file={f}
            selected={selectedFileId === f.fileId}
            onSelect={() => onSelect(f.fileId, f.originalFilename)}
            large
          />
        ))}
      </div>
    );
  }

  // Card-shell states aligned to ProjectFilesTab (review A10).
  if (isLoading) {
    return (
      <div className="rounded-lg border border-brown-100 bg-white p-6 text-center text-xs text-brown-300">
        Loading files…
      </div>
    );
  }
  if (isError) {
    return (
      <div className="rounded-lg border border-brown-100 bg-white p-6 text-center text-xs text-rust-500">
        Couldn't load project files.
      </div>
    );
  }
  if (rows.length === 0) {
    return (
      <div className="rounded-lg border border-brown-100 bg-white p-6 text-center text-xs text-brown-300">
        No files on this project yet. Use the Upload New tab — it saves here too.
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
      {rows.map((f) => (
        <ProjectFileTile
          key={f.id}
          projectId={projectId}
          file={f}
          selected={selectedFileId === f.fileId}
          onSelect={() => onSelect(f.fileId, f.originalFilename)}
        />
      ))}
    </div>
  );
}

function ProjectFileTile(props: {
  projectId: string;
  file: ProjectFileRow;
  selected: boolean;
  onSelect: () => void;
  large?: boolean;
}) {
  const { projectId, file, selected, onSelect, large } = props;
  const isImage = isImageMime(file.mimeType);
  const { data: download } = useQuery({
    queryKey: ["project", projectId, "file-download", file.fileId],
    queryFn: () =>
      api<DownloadResponse>(
        `/projects/${projectId}/files/${file.fileId}/download`,
      ),
    enabled: isImage,
    staleTime: 8 * 60 * 1000,
  });

  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        "overflow-hidden rounded-md border text-left transition-colors",
        selected
          ? cn("border-ink ring-ink", large ? "ring-2" : "ring-1")
          : "border-brown-50 hover:border-peach-300",
      )}
    >
      <div className={cn("flex items-center justify-center bg-cream-100", large ? "h-32" : "h-20")}>
        {isImage && download?.url ? (
          <img
            src={download.url}
            alt={file.originalFilename}
            className="h-full w-full object-contain"
          />
        ) : (
          <span className="font-display text-[10px] font-bold uppercase tracking-wide text-brown-300">
            {file.mimeType.split("/")[1] ?? "File"}
          </span>
        )}
      </div>
      <div
        className={cn("truncate px-1.5 py-1 text-brown-400", large ? "text-sm" : "text-[10px]")}
        title={file.originalFilename}
      >
        {file.originalFilename}
      </div>
    </button>
  );
}

// ─── Client Library — cross-project aggregator not built yet ──────────────

function ClientLibraryComingSoon(props: { onGoToUpload: () => void }) {
  return (
    <div className="rounded-lg border border-brown-100 bg-white px-3 py-6 text-center">
      <div className="mx-auto flex h-8 w-8 items-center justify-center rounded-full bg-cream-200 text-brown-400">
        <Library className="h-4 w-4" aria-hidden />
      </div>
      <p className="mx-auto mt-2 max-w-xs text-xs text-brown-400">
        Reuse art from anywhere in this account's library. Not wired up yet — for
        now, use{" "}
        <button
          type="button"
          onClick={props.onGoToUpload}
          className="font-semibold text-ink underline underline-offset-2 hover:text-muted"
        >
          Upload New
        </button>{" "}
        or the Project Files tab.
      </p>
    </div>
  );
}
